import { useState, useEffect, useCallback } from "react";
import { urls } from "../urls";
import { useUserStore } from "../state";
import { useAuth } from "./useAuth";
import { handleUnauthorized } from "utils/handleUnauthorized";
import { QueryRequestBody } from "interfaces/common/QueryRequestBody";

export interface QueueCount {
    transactionStatus: string;
    count: number;
}

export const useQueryDashboard = () => {
    const url = urls.QUERY;
    const [queueCounts, setQueueCounts] = useState<QueueCount[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<any>(null);

    const userConfig = useUserStore(state => state.userConfig);
    const config = useUserStore(state => state.config);
    const { isAdmin } = userConfig;
    const { hardLimit } = config.transactionViewing;
    const { logout } = useAuth();

    const fetchQueueCounts = useCallback(async (signal?: AbortSignal) => {

        const queryParams = [
            { name: "type", value: "Original" },
            { name: "isAdmin", value: isAdmin },
            { name: "days", value: hardLimit.toString() },
        ];

        const queryDataRequest: QueryRequestBody = {
            queries: [
                {
                    queryParams,
                    limit: 25,
                    offset: 0,
                    queryName: process.env.REACT_APP_DASHBOARD_QUEUES_COUNT_QUERY! ?? "query_MC_DashboardQueuesCount",
                    transformData: false,
                }
            ]
        };

        try {
            setLoading(true);
            setError(null);

            const transactionalUrl = `${process.env.NODE_ENV === "production" ? url : ""}/query`;

            const data = await (
                await fetch(transactionalUrl, {
                    method: "POST",
                    credentials: "include",
                    body: JSON.stringify(queryDataRequest),
                    signal,
                })
            ).json();

            handleUnauthorized(data.message, logout);

            if (data) {
                const results: QueueCount[] = data['queriesResults'][0].results;
                setQueueCounts(results);
            }
        } catch (err: any) {
            if (err.name !== "AbortError") {
                setError(err);
            }
        } finally {
            setLoading(false);
        }
    }, [isAdmin, hardLimit, url, logout]);

    useEffect(() => {
        const controller = new AbortController();
        fetchQueueCounts(controller.signal);
        return () => controller.abort();
    }, [fetchQueueCounts]);

    return { queueCounts, loading, error, refresh: fetchQueueCounts };
};